import { useLayoutEffect } from 'react';

// Head tags are rendered as plain <meta>/<link> elements: React 19 hoists them
// into <head> synchronously (react-helmet-async 2.x is unreliable on React 19).
// Every tag carries data-seo so index.js can strip the prerendered copies before
// the client render re-creates them.

const SEO = ({ 
  title = "Epoxy Flooring Naples FL | Garage Floor Coatings | EpoxyArt33",
  description = "Epoxy flooring & garage floor coatings in Naples FL. Flake, metallic & polyaspartic systems, lifetime residential warranty, 500+ projects. Free estimate: (239) 276-1462.",
  keywords = "epoxy flooring Naples FL, garage floor coating Naples FL, concrete coatings Naples, pool deck resurfacing Naples, epoxy art 33",
  noindex = false,
  image = "/images/projects/hero-metallic.webp",
  url = "",
  type = "website",
  // Optional structured data props
  serviceSchema = null,        // { name, description, areaServed }
  articleSchema = null,        // { headline, datePublished, dateModified, author, image }
  faqSchema = null,            // [{ question, answer }, ...]
  breadcrumbs = null           // [{ name, url }, ...]
}) => {
  // document.title is set imperatively (the static <title> in index.html is
  // the single title element; rendering another would duplicate it).
  useLayoutEffect(() => {
    if (typeof document !== 'undefined' && title) document.title = title;
  }, [title]);

  const siteUrl = "https://epoxyart33.com";
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Build canonical URL - always without .html and with proper domain
  let canonicalPath = url || "/";
  canonicalPath = canonicalPath.replace(/\.html$/, '');
  if (!canonicalPath.startsWith('/') && !canonicalPath.startsWith('http')) {
    canonicalPath = '/' + canonicalPath;
  }
  const fullUrl = canonicalPath.startsWith('http') ? canonicalPath : `${siteUrl}${canonicalPath}`;

  // ---------- Structured Data Generators ----------
  const schemas = [];

  if (serviceSchema) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": serviceSchema.name,
      "description": serviceSchema.description || description,
      "serviceType": serviceSchema.name,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Epoxy Art 33",
        "alternateName": "EpoxyArt33",
        "telephone": "+1-239-276-1462",
        "url": siteUrl,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "160 15th St SW",
          "addressLocality": "Naples",
          "addressRegion": "FL",
          "postalCode": "34117",
          "addressCountry": "US"
        }
      },
      "areaServed": (serviceSchema.areaServed || ["Naples FL", "North Naples FL", "Golden Gate Estates FL", "Marco Island FL", "Bonita Springs FL", "Estero FL"]).map(a => ({
        "@type": "City",
        "name": a
      })),
      "url": fullUrl,
      "image": fullImageUrl
    });
  }

  if (articleSchema) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": articleSchema.headline || title,
      "description": description,
      "image": fullImageUrl,
      "datePublished": articleSchema.datePublished,
      "dateModified": articleSchema.dateModified || articleSchema.datePublished,
      "author": {
        "@type": "Organization",
        "name": articleSchema.author || "Epoxy Art 33",
        "url": siteUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "Epoxy Art 33",
        "alternateName": "EpoxyArt33",
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/logos/logo-wordmark.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": fullUrl
      }
    });
  }

  if (faqSchema && Array.isArray(faqSchema) && faqSchema.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqSchema.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
  }

  if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": b.name,
        "item": b.url.startsWith('http') ? b.url : `${siteUrl}${b.url}`
      }))
    });
  }

  return (
    <>
      {/* Primary Meta Tags */}
      <meta data-seo="" name="title" content={title} />
      <meta data-seo="" name="description" content={description} />
      <meta data-seo="" name="keywords" content={keywords} />
      {!noindex && <link data-seo="" rel="canonical" href={fullUrl} />}

      {/* Open Graph / Facebook */}
      <meta data-seo="" property="og:type" content={type} />
      <meta data-seo="" property="og:url" content={fullUrl} />
      <meta data-seo="" property="og:title" content={title} />
      <meta data-seo="" property="og:description" content={description} />
      <meta data-seo="" property="og:image" content={fullImageUrl} />
      <meta data-seo="" property="og:site_name" content="Epoxy Art 33" />
      <meta data-seo="" property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta data-seo="" property="twitter:card" content="summary_large_image" />
      <meta data-seo="" property="twitter:url" content={fullUrl} />
      <meta data-seo="" property="twitter:title" content={title} />
      <meta data-seo="" property="twitter:description" content={description} />
      <meta data-seo="" property="twitter:image" content={fullImageUrl} />

      {/* Additional SEO */}
      <meta data-seo="" name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta data-seo="" name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Geo Tags */}
      <meta data-seo="" name="geo.region" content="US-FL" />
      <meta data-seo="" name="geo.placename" content="Naples" />
      <meta data-seo="" name="geo.position" content="26.1420;-81.7948" />
      <meta data-seo="" name="ICBM" content="26.1420, -81.7948" />

      {/* Business Info */}
      <meta data-seo="" name="author" content="Epoxy Art 33" />

      {/* JSON-LD Structured Data (inline; valid anywhere in the document) */}
      {schemas.map((schema, i) => (
        <script key={i} data-seo="" type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </>
  );
};

export default SEO;
